import { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import api from '@/lib/api';
import { staffPositions as staffPositionsApi } from '@/lib/trackizy';
import { smoothFuelData, detectFuelEvents } from '@/features/fuel/fuelUtils';

// `target` field gates the layer:
//   'both'     → works for vehicles + staff (positions)
//   'vehicle'  → vehicle-only (alerts/fuel/stops)
//   'staff'    → staff-only (sos/checkins)
// HeatmapControl filters pills by the currently-selected target.
const DATA_LAYERS = [
  { id: 'positions', labelKey: 'heatmap.positions', descKey: 'heatmap.positionsDesc', target: 'both' },
  { id: 'alerts',    labelKey: 'heatmap.alerts',    descKey: 'heatmap.alertsDesc',    target: 'vehicle' },
  { id: 'fuel',      labelKey: 'heatmap.fuel',      descKey: 'heatmap.fuelDesc',      target: 'vehicle' },
  { id: 'stops',     labelKey: 'heatmap.stops',     descKey: 'heatmap.stopsDesc',     target: 'vehicle' },
  { id: 'sos',       labelKey: 'heatmap.sos',       descKey: 'heatmap.sosDesc',       target: 'staff' },
  { id: 'checkins',  labelKey: 'heatmap.checkins',  descKey: 'heatmap.checkinsDesc',  target: 'staff' },
];

const PRESETS = {
  today: () => { const now = new Date(); const start = new Date(now); start.setHours(0,0,0,0); return { from: start.toISOString(), to: now.toISOString() }; },
  yesterday: () => { const y = new Date(); y.setDate(y.getDate()-1); const start = new Date(y); start.setHours(0,0,0,0); const end = new Date(y); end.setHours(23,59,59,999); return { from: start.toISOString(), to: end.toISOString() }; },
  '7days': () => { const now = new Date(); const start = new Date(now); start.setDate(start.getDate()-7); start.setHours(0,0,0,0); return { from: start.toISOString(), to: now.toISOString() }; },
  '30days': () => { const now = new Date(); const start = new Date(now); start.setDate(start.getDate()-30); start.setHours(0,0,0,0); return { from: start.toISOString(), to: now.toISOString() }; },
};

export { DATA_LAYERS, PRESETS };

export default function useHeatmap() {
  const devices = useSelector((state) => state.devices.items);
  const [loading, setLoading] = useState(false);
  const [geojson, setGeojson] = useState(null);
  const [stats, setStats] = useState({ points: 0, vehicles: 0 });
  const cache = useRef({});

  // Fetch positions for devices in batches of 5
  const fetchBatched = useCallback(async (deviceIds, fetchFn) => {
    const results = [];
    for (let i = 0; i < deviceIds.length; i += 5) {
      const batch = deviceIds.slice(i, i + 5);
      const batchResults = await Promise.allSettled(batch.map(fetchFn));
      batchResults.forEach((r) => {
        if (r.status === 'fulfilled' && r.value) results.push(...(Array.isArray(r.value) ? r.value : [r.value]));
      });
    }
    return results;
  }, []);

  // Convert positions to GeoJSON with dwell-time weight
  const positionsToGeoJSON = (positions) => {
    const features = [];
    for (let i = 0; i < positions.length; i++) {
      const p = positions[i];
      if (!p.latitude || !p.longitude) continue;
      // Weight = time gap to next position (dwell time), normalized
      let weight = 0.3; // default
      if (i < positions.length - 1) {
        const gap = (new Date(positions[i+1].fixTime) - new Date(p.fixTime)) / 1000;
        weight = Math.min(gap / 600, 1); // normalize: 10min gap = max weight
      }
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
        properties: { weight },
      });
    }
    return { type: 'FeatureCollection', features };
  };

  // Convert stops to GeoJSON with duration weight
  const stopsToGeoJSON = (stops) => {
    const maxDuration = Math.max(...stops.map((s) => s.duration || 0), 1);
    return {
      type: 'FeatureCollection',
      features: stops.filter((s) => s.latitude && s.longitude).map((s) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [s.longitude, s.latitude] },
        properties: { weight: Math.min((s.duration || 0) / maxDuration, 1) },
      })),
    };
  };

  // Convert events to GeoJSON (equal weight — density based)
  // Traccar events carry positionId but NOT lat/lng directly.
  // event.attributes MAY have latitude/longitude for some event types, but most don't.
  // For heatmap, we fetch positions for all positionIds to get coordinates.
  const eventsToGeoJSON = async (events, from, to, deviceIds) => {
    // First try event.attributes for coords (some event types include them)
    // Then fall back to fetching positions for events that have positionId
    const features = [];
    const needPositionLookup = [];

    events.forEach((e) => {
      if (e.attributes?.latitude && e.attributes?.longitude) {
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [e.attributes.longitude, e.attributes.latitude] },
          properties: { weight: 1.0 },
        });
      } else if (e.positionId) {
        needPositionLookup.push(e);
      }
    });

    // If we have events needing position lookup, fetch route positions and match by timestamp
    if (needPositionLookup.length > 0 && deviceIds.length > 0) {
      // Fetch positions for the time range (already fetched for heatmap, could cache)
      // Match events to nearest position by eventTime
      const allPositions = await fetchBatched(deviceIds, (id) =>
        api.getRoute({ deviceId: id, from, to })
      );
      const posMap = new Map(allPositions.map((p) => [p.id, p]));

      needPositionLookup.forEach((e) => {
        const pos = posMap.get(e.positionId);
        if (pos?.latitude && pos?.longitude) {
          features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [pos.longitude, pos.latitude] },
            properties: { weight: 1.0 },
          });
        }
      });
    }

    return { type: 'FeatureCollection', features };
  };

  // Process fuel events PER DEVICE (smoothing/detection must run on single-device streams)
  // Returns GeoJSON from per-device fuel event detection
  const processFuelPerDevice = (allDevicePositions) => {
    // allDevicePositions is a Map: deviceId → positions[]
    const features = [];
    for (const positions of allDevicePositions.values()) {
      if (!positions.length) continue;
      const smoothed = smoothFuelData(positions);
      const fuelEvents = detectFuelEvents(smoothed);
      // detectFuelEvents returns startIndex into pts = smoothed.filter(p => p._smoothedFuel != null)
      // We need to resolve back to the filtered array for correct coordinates
      const pts = smoothed.filter((p) => p._smoothedFuel != null);
      fuelEvents.forEach((event) => {
        const pos = pts[event.startIndex];
        if (!pos?.latitude || !pos?.longitude) return;
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [pos.longitude, pos.latitude] },
          properties: { weight: 1.0 },
        });
      });
    }
    return { type: 'FeatureCollection', features };
  };

  // Fetch staff positions for the time window and convert directly to
  // GeoJSON. Staff history rows have { latitude, longitude, fixTime }, the
  // same shape positionsToGeoJSON expects after a small rename pass.
  const fetchStaffPositions = useCallback(async ({ userId, from, to }) => {
    try {
      const res = await staffPositionsApi.history({ from, to, userId });
      const rows = (res?.positions ?? []).map((p) => ({
        latitude: p.latitude,
        longitude: p.longitude,
        fixTime: p.fixTime,
      }));
      return rows;
    } catch (err) {
      console.warn('Staff position history failed:', err);
      return [];
    }
  }, []);

  const generate = useCallback(async ({ layer, deviceId, userId, from, to }) => {
    const cacheKey = `${layer}-${deviceId ? `v:${deviceId}` : userId ? `s:${userId}` : 'all'}-${from}-${to}`;
    if (cache.current[cacheKey]) {
      setGeojson(cache.current[cacheKey].geojson);
      setStats(cache.current[cacheKey].stats);
      return;
    }

    setLoading(true);
    try {
      // Vehicle deviceIds — empty when a specific staff user is chosen.
      const deviceIds = userId
        ? []
        : (deviceId ? [deviceId] : Object.keys(devices).map(Number));
      let result;
      let vehiclesWithData = 0;

      if (layer === 'positions') {
        const vehiclePositions = deviceIds.length
          ? await fetchBatched(deviceIds, (id) => api.getRoute({ deviceId: id, from, to }))
          : [];
        // Staff are only included for positions layer (the other layers —
        // alerts/fuel/stops — are vehicle-specific and don't apply yet).
        const staffWanted = userId || (!deviceId && !userId); // staff user OR "All"
        const staffPositionsRows = staffWanted
          ? await fetchStaffPositions({ userId: userId || null, from, to })
          : [];
        const allPositions = [...vehiclePositions, ...staffPositionsRows];
        result = positionsToGeoJSON(allPositions);
        vehiclesWithData = deviceIds.length + (staffPositionsRows.length > 0 ? 1 : 0);
      } else if (layer === 'alerts') {
        // Don't pass type param — Traccar returns all event types when type is omitted
        const allEvents = await fetchBatched(deviceIds, (id) =>
          api.getEvents({ deviceId: id, from, to })
        );
        // eventsToGeoJSON is async — fetches positions for events without coords
        result = await eventsToGeoJSON(allEvents, from, to, deviceIds);
        vehiclesWithData = deviceIds.length;
      } else if (layer === 'fuel') {
        // Fuel must be processed PER DEVICE (smoothing crosses device boundaries if merged)
        const devicePositions = new Map();
        for (let i = 0; i < deviceIds.length; i += 5) {
          const batch = deviceIds.slice(i, i + 5);
          const batchResults = await Promise.allSettled(
            batch.map((id) => api.getPositions({ deviceId: id, from, to }).then((p) => [id, p]))
          );
          batchResults.forEach((r) => {
            if (r.status === 'fulfilled' && r.value) {
              const [id, positions] = r.value;
              if (positions?.length) devicePositions.set(id, positions);
            }
          });
        }
        result = processFuelPerDevice(devicePositions);
        vehiclesWithData = devicePositions.size;
      } else if (layer === 'stops') {
        const allStops = await fetchBatched(deviceIds, (id) =>
          api.getStops({ deviceId: id, from, to })
        );
        result = stopsToGeoJSON(allStops);
        vehiclesWithData = deviceIds.length;
      } else if (layer === 'sos' || layer === 'checkins') {
        // Staff-only event layers. If user picked a vehicle deviceId we
        // return no data — staff-only layers don't apply to vehicles.
        if (deviceId) {
          result = { type: 'FeatureCollection', features: [] };
        } else {
          const fn = layer === 'sos' ? staffPositionsApi.sosHistory : staffPositionsApi.checkinHistory;
          let rows = [];
          try {
            const res = await fn({ from, to, userId: userId || null });
            rows = res?.events ?? [];
          } catch (err) {
            console.warn(`Heatmap ${layer} fetch failed:`, err);
          }
          result = {
            type: 'FeatureCollection',
            features: rows
              .filter((e) => e.latitude != null && e.longitude != null)
              .map((e) => ({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [e.longitude, e.latitude] },
                properties: { weight: 1.0 },
              })),
          };
          vehiclesWithData = rows.length > 0 ? 1 : 0;
        }
      }

      const newStats = { points: result?.features?.length || 0, vehicles: vehiclesWithData };
      cache.current[cacheKey] = { geojson: result, stats: newStats };
      setGeojson(result);
      setStats(newStats);
    } catch (err) {
      console.error('Heatmap generation failed:', err);
      setGeojson(null);
      setStats({ points: 0, vehicles: 0 });
    } finally {
      setLoading(false);
    }
  }, [devices, fetchBatched, fetchStaffPositions]);

  const clear = useCallback(() => {
    setGeojson(null);
    setStats({ points: 0, vehicles: 0 });
  }, []);

  return { generate, clear, loading, geojson, stats, DATA_LAYERS, PRESETS };
}
